import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPlan, previewPlan } from "../api/endpoints";
import {
  SESSION_FOCUSES,
  SESSION_FOCUS_LABELS,
  SESSION_TYPES,
  SESSION_TYPE_LABELS,
  type SessionFocus,
  type SessionType,
  formatTarget,
  type PlannedSession,
} from "../api/types";
import { ApiError } from "../api/client";
import { Button, Card, ErrorMessage, PageHeading } from "../components/ui";

const selectClass =
  "mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-600 focus:outline-none dark:border-[#2e2e2e] dark:bg-[#111111] dark:text-gray-100";

export default function PlanSessionPage() {
  const navigate = useNavigate();
  const [sessionType, setSessionType] = useState<SessionType>("kettlebell");
  const [focus, setFocus] = useState<SessionFocus>("balanced");
  const [rounds, setRounds] = useState(3);
  const [preview, setPreview] = useState<PlannedSession | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  const request = { sessionType, focus, rounds };

  async function run<T>(fn: () => Promise<T>, onOk: (value: T) => void) {
    setBusy(true);
    setError(null);
    try {
      onOk(await fn());
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  const isEmptyCatalogue =
    error instanceof ApiError && /no exercises|empty|not enough/i.test(error.message);

  return (
    <div data-testid="plan-page">
      <PageHeading title="Plan a session" />

      <Card className="p-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="sessionType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Session type
            </label>
            <select
              id="sessionType"
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value as SessionType)}
              className={selectClass}
              data-testid="plan-select-type"
            >
              {SESSION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {SESSION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="focus" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Focus
            </label>
            <select
              id="focus"
              value={focus}
              onChange={(e) => setFocus(e.target.value as SessionFocus)}
              className={selectClass}
              data-testid="plan-select-focus"
            >
              {SESSION_FOCUSES.map((f) => (
                <option key={f} value={f}>
                  {SESSION_FOCUS_LABELS[f]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="rounds" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Rounds
            </label>
            <input
              id="rounds"
              type="number"
              min={1}
              max={20}
              value={rounds}
              onChange={(e) => setRounds(Number(e.target.value))}
              className={selectClass}
              data-testid="plan-input-rounds"
            />
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => void run(() => previewPlan(request), setPreview)}
            data-testid="plan-button-preview"
          >
            {preview ? "Regenerate" : "Preview"}
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void run(
                () => createPlan(request),
                (created) => navigate(`/sessions/${created.session.id}`),
              )
            }
            data-testid="plan-button-create"
          >
            Create session
          </Button>
        </div>

        {error && (
          <div className="mt-4">
            <ErrorMessage error={error} />
            {isEmptyCatalogue && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                The exercise catalogue looks empty, so there is nothing to plan from.
              </p>
            )}
          </div>
        )}
      </Card>

      {preview && (
        <Card className="mt-4 p-5" data-testid="plan-preview">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Preview — not saved yet
          </h2>
          <ol className="mt-3 divide-y divide-gray-100 dark:divide-[#2e2e2e]">
            {preview.exercises.map((ex) => (
              <li key={ex.exerciseId} className="flex justify-between py-2.5 text-sm">
                <span className="text-gray-900 dark:text-gray-100">{ex.exerciseName}</span>
                <span className="text-gray-500 dark:text-gray-500">
                  {formatTarget(ex)}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
